import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { COLORS, FONT_FAMILY } from '../constants/constants';
import Button from '@mui/material/Button';
import { Box, Container, TextField, Typography, Grid, lighten, darken } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

export const IframeSupport = () => {
    const { t } = useTranslation()
    const form = useRef();
    const [message, setMessage] = useState('');
    const [charCount, setCharCount] = useState(0);

    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.5  // Ajusta según necesidad para cuándo debe activarse la animación
    });

    useEffect(() => {
        setCharCount(message.length);
    }, [message]);

    const sendEmail = (e) => {
        e.preventDefault();

        // Simple validation for empty fields
        if (!form.current.from_name.value.trim() ||
            !form.current.from_email.value.trim()) {
            alert(t('IframeSupport00'));
            return;
        }

        // Simple email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.current.from_email.value)) {
            alert(t('IframeSupport01'));
            return;
        }

        emailjs.sendForm('service_60er2i5', 'template_r85kjm7', form.current, 'cDo2OZYTwYI3-SbGV')
            .then((result) => {
                console.log(result.text);
                alert(t('IframeSupport02'));
                form.current.reset(); // Reset the form fields to their original state
            }, (error) => {
                console.log(error.text);
                alert(t('IframeSupport03'));
            });
    };

    const handleMessageChange = (e) => {
        if (e.target.value.length <= 300) {
            setMessage(e.target.value);
            setCharCount(e.target.value.length);
        }
    };

    const fieldData = [
        {
            label: t('IframeSupport04'),
            type: "text",
            name: "from_name",
            placeholder: t('IframeSupport05'),
        },
        {
            label: t('IframeSupport06'),
            type: "email",
            name: "from_email",
            placeholder: t('IframeSupport07'),
        },
    ];

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                px: { xs: 2, md: 4 },
                backgroundColor: 'background.default',
            }}
        >
            <Container maxWidth="md">
                <form ref={form} onSubmit={sendEmail}>
                    {/* Header Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: 2,
                            mb: 6,
                        }}
                        ref={ref}
                    >
                        <Typography 
                            variant="h2"
                            sx={{
                                fontFamily: 'Barlow, sans-serif',
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
                                fontWeight: 800,
                                lineHeight: 1.2,
                                color: '#1a202c',
                            }}
                        >
                            {t('IframeSupport08')}
                        </Typography>

                        <Typography
                            sx={{
                                color: 'text.secondary',
                                maxWidth: 500,
                                fontFamily: 'Barlow, sans-serif',
                                fontSize: '1rem',
                                lineHeight: 1.5,
                            }}
                        >
                            {t('IframeSupport09')}
                        </Typography>
                    </Box>

                    {/* Form Container */}
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        {/* Input Fields */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {fieldData.map((field, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <TextField
                                        type={field.type}
                                        name={field.name}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        fullWidth
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: 'rgba(0, 0, 0, 0.1)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF7720',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF7720',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontFamily: 'Barlow, sans-serif',
                                                '&.Mui-focused': {
                                                    color: '#FF7720',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontFamily: 'Barlow, sans-serif',
                                                fontSize: '0.95rem',
                                            },
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Message Field */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                name="message"
                                label={t('IframeSupport10')}
                                multiline
                                rows={4}
                                variant="outlined"
                                fullWidth
                                value={message}
                                onChange={handleMessageChange}
                                placeholder={t('IframeSupport11')}
                                helperText={`${charCount}/300`}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '& fieldset': {
                                            borderColor: 'rgba(0, 0, 0, 0.1)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#FF7720',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF7720',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: 'Barlow, sans-serif',
                                        '&.Mui-focused': {
                                            color: '#FF7720',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'Barlow, sans-serif',
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontFamily: 'Barlow, sans-serif',
                                        fontSize: '0.75rem',
                                        color: '#4a5568',
                                    },
                                }}
                            />
                        </Box>

                        {/* Submit Button */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    background: '#1a202c',
                                    color: 'white',
                                    px: 2,
                                    py: 0.5,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    boxShadow: '0 2px 8px rgba(26, 32, 44, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(26, 32, 44, 0.3)',
                                        background: '#1a202c',
                                    },
                                }}
                            >
                                {t('IframeSupport12')}
                                <Box component="span" sx={{ fontSize: '1rem' }}>→</Box>
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Container>
        </Box>
    );
};